'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, ShoppingBag, Award, ShieldCheck, TrendingUp, Landmark, BarChart3, Users, UserPlus, FileBarChart, Settings, ChevronLeft, ChevronRight, Building2, Globe, Package, CheckSquare, AlertTriangle, BookOpen, Briefcase, Eye, DollarSign, MapPin, ClipboardList, UserCheck, Layers, Handshake } from 'lucide-react';
import { useRole } from '@/context/RoleContext';

const ICON_MAP: Record<string, React.ElementType> = {
  'Dashboard': LayoutDashboard,
  'Company Profile': Building2,
  'Product Listings': Package,
  'Certification': Award,
  'Finance Matching': Landmark,
  'Business Matching': Handshake,
  'Trade Intelligence': BarChart3,
  'Reports': FileBarChart,
  'Settings': Settings,
  'Supplier Marketplace': ShoppingBag,
  'RFQ Management': ClipboardList,
  'Certificate Verification': ShieldCheck,
  'Supplier Comparison': Layers,
  'Investment Exchange': TrendingUp,
  'Due Diligence Room': BookOpen,
  'Watchlist': Eye,
  'Certificate Applications': CheckSquare,
  'Registry': Award,
  'QR Verification': ShieldCheck,
  'Audit Trail': ClipboardList,
  'Recognition Mapping': MapPin,
  'Compliance Review': AlertTriangle,
  'Funding Requests': DollarSign,
  'Product Matching': Layers,
  'SME Trust Review': UserCheck,
  'Takaful Requests': Briefcase,
  'Partner Pipeline': Users,
  'SME Readiness': BarChart3,
  'Certification Analytics': Award,
  'Investment Activity': TrendingUp,
  'Country Reports': Globe,
  'Policy Reports': FileBarChart,
  'Compliance Oversight': ShieldCheck,
  'Onboarding': UserPlus,
  'Partner Management': Users,
  'Marketplace Review': ShoppingBag,
  'Certificate Registry': Award,
  'Risk Monitoring': AlertTriangle,
  'User Management': Users,
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeRoute?: string;
}

export default function Sidebar({ collapsed, onToggle, activeRoute }: SidebarProps) {
  const { roleConfig } = useRole();

  return (
    <aside
      className="flex flex-col h-full border-r transition-all duration-300 shrink-0 scrollbar-thin overflow-y-auto"
      style={{
        width: collapsed ? '64px' : '240px',
        background: 'var(--secondary)',
        borderColor: 'var(--border)',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-3 px-4 py-4 border-b"
        style={{ borderColor: 'var(--border)', minHeight: '64px' }}
      >
        <AppLogo src="/assets/images/Halalchamp_png-1783699489068.png" size={32} className="shrink-0" />
        {!collapsed && (
          <span className="font-bold text-sm tracking-wide truncate" style={{ color: 'var(--gold)' }}>
            HalalCham
          </span>
        )}
      </div>

      {/* Role Badge */}
      {!collapsed && (
        <div
          className="mx-3 mt-3 mb-1 px-3 py-2 rounded-lg"
          style={{ background: `${roleConfig.color}12`, border: `1px solid ${roleConfig.color}30` }}
        >
          <p className="text-xs font-semibold truncate" style={{ color: roleConfig.color }}>
            {roleConfig.accountLabel}
          </p>
          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            {roleConfig.profileName}
          </p>
        </div>
      )}

      {/* Nav Groups */}
      <nav className="flex-1 py-3 px-2 space-y-4">
        {roleConfig.navGroups.map((group) => (
          <div key={`group-${group.label}`}>
            {!collapsed && (
              <p
                className="px-2 mb-2 text-xs font-600 uppercase tracking-widest"
                style={{ color: 'var(--muted-foreground)', letterSpacing: '0.08em' }}
              >
                {group.label}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => {
                const isActive = activeRoute === item.href || (activeRoute === undefined && item.href === roleConfig.dashboardPath);
                const IconComp = ICON_MAP[item.label] || LayoutDashboard;
                return (
                  <li key={`nav-${item.label}`}>
                    <Link
                      href={item.href}
                      className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative group ${
                        isActive ? 'nav-item-active' : 'nav-item-hover'
                      }`}
                      style={{ color: isActive ? 'var(--emerald-bright)' : 'var(--secondary-foreground)' }}
                      title={collapsed ? item.label : undefined}
                    >
                      <IconComp
                        size={18}
                        className="shrink-0"
                        style={{ color: isActive ? 'var(--emerald-bright)' : 'var(--muted-foreground)' }}
                      />
                      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                      {!collapsed && item.badge && (
                        <span
                          className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ background: 'rgba(10, 107, 79, 0.2)', color: 'var(--emerald-bright)', minWidth: '20px', textAlign: 'center' }}
                        >
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && (
                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: 'var(--emerald-bright)' }} />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse Toggle */}
      <div className="p-3 border-t" style={{ borderColor: 'var(--border)' }}>
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center p-2 rounded-lg transition-all duration-200 btn-outline"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="ml-2 text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}